import type { Hours, MenuHours } from '@centric-os/types';
import { DateTime, IANAZone, Interval } from 'luxon';

export default ({ timezone }: { timezone?: string } = {}) => {
  type IntervalEntry = { hours: Interval; endDate: string };

  if (!timezone) timezone = DateTime.local().zoneName;

  const local = () => {
    return DateTime.local().setZone(timezone);
  };

  const fromISO = (date: string): DateTime => {
    return DateTime.fromISO(date, { zone: timezone });
  };

  /**
   * Parse an interval string like "HH:mm-HH:mm" on the given date into a Luxon Interval.
   * Supports overnight ranges (e.g. "23:00-02:00").
   */
  const parseHoursToInterval = (hours: string, date: string): Interval => {
    const [startStr, endStr] = hours.split('-');
    const [startHour, startMinute] = startStr.split(':').map(Number);
    const [endHour, endMinute] = endStr.split(':').map(Number);

    // Anchor on the entry's own date: a time in another day's skipped DST hour would shift an hour
    const startOfDate = fromISO(date);
    let start = startOfDate.set({ hour: startHour, minute: startMinute });
    let end = startOfDate.set({ hour: endHour, minute: endMinute });

    // If end is before or equal to start, assume it rolls over past midnight
    if (end <= start) {
      end = end.plus({ days: 1 });
    }
    return Interval.fromDateTimes(start, end);
  };

  /**
   * Parse a Luxon Interval into a string like "HH:mm-HH:mm".
   */
  const parseIntervalToHours = (interval: Interval): string => {
    return interval.start.toFormat('HH:mm') + '-' + interval.end.toFormat('HH:mm');
  };

  /**
   * Check if the date is today.
   * @param date - The date to check (can be a string in 'yyyy-MM-dd' format or DateTime object).
   * @returns True if the date is today, false otherwise.
   */
  const isToday = (date: string | DateTime): boolean => {
    const dateTime = typeof date === 'string' ? fromISO(date) : date;
    const now = local();
    return dateTime.toFormat('yyyy-MM-dd') === now.toFormat('yyyy-MM-dd');
  };

  /**
   * Check if the date is in the past.
   * @param date - The date to check (can be a string in 'yyyy-MM-dd' format or DateTime object).
   * @returns True if the date is in the past, false otherwise.
   */
  const isInPast = (date: string | DateTime): boolean => {
    const dateTime = typeof date === 'string' ? fromISO(date) : date;
    const today = local().startOf('day');
    return dateTime < today;
  };

  /**
   * Returns true if the menu hours are in the past.
   *
   * Past counts as:
   * - The end date being before today
   * - If includeTodaysHoursIfPassed = true, then return true for menu hours starting today and having already passed.
   * @param menuHour - The menu hours to check.
   * @param includeTodaysHoursIfPassed - Whether to return true if today's hours have already passed.
   * @returns True if the menu hours are in the past, false otherwise.
   */
  const areMenuHoursInPast = (
    menuHour: MenuHours,
    includeTodaysHoursIfPassed: boolean = false,
  ): boolean => {
    const startDateIsToday = isToday(menuHour.date.start);
    const endDateIsBeforeToday = isInPast(menuHour.date.end);
    if (includeTodaysHoursIfPassed && startDateIsToday) {
      return areTodaysHoursAlreadyPassed(menuHour.hours);
    }
    return endDateIsBeforeToday;
  };

  /**
   * Check if the opening hours represent 24-hour opening.
   * @param hours - The hours to check. (String formatted as "HH:mm-HH:mm")
   * @returns True if the hours are 24 hours, false otherwise.
   */
  const isOpen24Hours = (hours: string): boolean => {
    const [start, end] = hours.split('-');
    const [startHour, startMinute] = start.split(':').map(Number);
    const [endHour, endMinute] = end.split(':').map(Number);
    return startHour === endHour && startMinute === endMinute;
  };

  /**
   * Takes menu hours in local timezone and returns ones that have not passed.
   *
   * A set of menu hours is considered "passed" if:
   * - The end date is before today
   * - If includeTodaysHoursIfPassed = true, then menu hours starting today and having already passed.
   *
   * Generally, set includeTodaysHoursIfPassed = true to display singular menu hours (eg. when expanding a station)
   * and false when combining menu hours, to preserve today's menu hours if they can be merged with others.
   * (Eg: If time now is 9am and hours are [5am-8am, 8am-11am], we preserve 5am-8am to show "5am - 11am" for station.)
   * (But for a single menu with hours 5am-8am, we will not show that for today.)
   * @param menuHours Menu hours in local timezone.
   * @param filterTodayHoursIfPassed Whether to filter out today's hours if they have already passed.
   * @returns Menu hours that end on today or later.
   */
  const filterPastMenuHours = (
    menuHours: MenuHours[],
    filterTodayHoursIfPassed: boolean = false,
  ): Hours[] => {
    return menuHours.filter((menuHour) => {
      return !areMenuHoursInPast(menuHour, filterTodayHoursIfPassed);
    });
  };

  /**
   * For a list of menu hours under a station, localize all menu hours in the given timezone.
   * @param allMenuHoursInStation - A list of menu hours, each represented as an array of hours.
   * @returns Same list of menu hours, but with hours in the given timezone.
   */
  const localizeAllMenuHours = (allMenuHoursInStation: MenuHours[][]): MenuHours[][] => {
    return allMenuHoursInStation.map((menuHours: MenuHours[]) => {
      return filterPastMenuHours(convertUtcHours(menuHours));
    });
  };

  /**
   * Group the hours for all menu schedules by start date, given a list of menu hours.
   *
   * @param allMenuHoursInStation - A list of menu hours, each represented as an array of hours.
   * example for a station with 2 menu schedules:
   * ```js
   * [
   *  [ // <- menu schedule 1
   *    { date: { start: '2025-01-01', end: '2025-01-01' }, hours: '10:00-12:00' },
   *    { date: { start: '2025-01-02', end: '2025-01-02' }, hours: '14:00-16:00' },
   *  ],
   *  [ // <- menu schedule 2
   *    { date: { start: '2025-01-01', end: '2025-01-01' }, hours: '13:00-15:00' },
   *    { date: { start: '2025-01-02', end: '2025-01-02' }, hours: '17:00-18:00' },
   *  ],
   * ]
   * ```
   *
   * @returns A dictionary indexed by start date, and value as a list of hours over all menu schedules for that day.
   * example:
   * ```js
   * {
   *  '2025-01-01': [
   *    { hours: [10:00-12:00, 13:00-15:00], endDate: '2025-01-01' },
   *  ],
   *  '2025-01-02': [
   *    { hours: [14:00-16:00, 17:00-18:00], endDate: '2025-01-02' },
   *  ],
   * }
   * ```
   */
  const groupMenuHoursByDay = (
    allMenuHoursInStation: MenuHours[][],
  ): Record<string, IntervalEntry[]> => {
    if (!allMenuHoursInStation?.length) return {};
    const allMenuHoursByDay: Record<string, IntervalEntry[]> = {};
    // Go through all scheduled menus
    allMenuHoursInStation.forEach((menuHours) => {
      // For each scheduled menu, add the hours for each day to the list of scheduled hours for that day.
      menuHours.forEach((menuHourDayEntry) => {
        const { date, hours } = menuHourDayEntry;
        if (!allMenuHoursByDay[date.start]) {
          allMenuHoursByDay[date.start] = [];
        }
        allMenuHoursByDay[date.start].push({
          hours: parseHoursToInterval(hours, date.start),
          endDate: date.end,
        });
      });
    });
    return allMenuHoursByDay;
  };

  /**
   * Determine if the new interval should replace the current interval.
   *
   * newInterval should replace currentInterval if:
   * - currentInterval is null
   * - newInterval is a 24 hour interval
   * - if intervals are for today's date and newInterval includes the current time without overlapping with currentInterval
   * - otherwise, choose newInterval if it is before currentInterval
   *
   * @param currentInterval - The current interval.
   * @param newInterval - The new interval.
   * @param startDateTime - The start date time.
   * @returns True if the new interval should replace the current interval, false otherwise.
   */
  const shouldReplaceHours = (
    currentInterval: Interval,
    newInterval: Interval,
    startDateTime: DateTime,
  ): boolean => {
    // If there is no current interval, add it.
    const now = local();
    if (!currentInterval) return true;
    const isTodayDate = isToday(startDateTime);
    const currentHasNow = currentInterval.contains(now);
    const newHasNow = newInterval.contains(now);
    const newHoursBeforeCurrent =
      newInterval.isBefore(currentInterval.start) ||
      // We count these hours as "after" if it has already passed today
      (isTodayDate && currentInterval.isBefore(now));
    const newHoursAre24hr =
      isOpen24Hours(parseIntervalToHours(newInterval)) &&
      (!isOpen24Hours(parseIntervalToHours(currentInterval)) ||
        newInterval.start.toMillis() < currentInterval?.start.toMillis());
    const newHoursAfterNow = newInterval.isAfter(now);
    const intervalsTouchOrOverlap = shouldCombineHours(currentInterval, newInterval);
    const shouldReplace =
      newHoursAre24hr || // new hours are 24 hours
      // for other days: prioritize earliest hours without overlap - or else we would merge instead
      (!isTodayDate && newHoursBeforeCurrent && !intervalsTouchOrOverlap) ||
      // for today:
      (isTodayDate &&
        // prioritize hours that contain now without overlapping
        newHasNow &&
        !currentHasNow &&
        !intervalsTouchOrOverlap) ||
      // or the earliest hours after now
      (newHoursBeforeCurrent && newHoursAfterNow && !intervalsTouchOrOverlap);
    return shouldReplace;
  };

  /**
   * Determine if the two intervals should be combined.
   *
   * Two intervals should be combined if:
   * - they both exist, AND
   * - they touch or overlap
   * @param currentInterval - The current interval.
   * @param newInterval - The new interval.
   * @returns True if the two intervals should be combined, false otherwise.
   */
  const shouldCombineHours = (currentInterval: Interval, newInterval: Interval): boolean => {
    return (
      !!currentInterval &&
      (newInterval.overlaps(currentInterval) ||
        newInterval.abutsStart(currentInterval) ||
        newInterval.abutsEnd(currentInterval) ||
        currentInterval.abutsStart(newInterval) ||
        currentInterval.abutsEnd(newInterval))
    );
  };

  const computeStationHoursForSingleDay = (
    intervals: IntervalEntry[],
    startDate: string,
  ): IntervalEntry => {
    if (!intervals?.length) return { hours: null, endDate: null };
    const startDateTime = fromISO(startDate);
    // Sort menu hours by start time.
    const sortedIntervals = intervals.sort(
      (a, b) => a.hours.start.toMillis() - b.hours.start.toMillis(),
    );
    let result: IntervalEntry = sortedIntervals[0]; // result = station hours we return. defaults to first menu hours(earliest).
    for (let i = 1; i < sortedIntervals.length; i++) {
      // each iteration, we determine whether to use result, next, or combine them.
      const next: IntervalEntry = sortedIntervals[i];
      // if menu hours are disjoint, choose to replace with earlier one.
      const replaceWithNext = shouldReplaceHours(result.hours, next.hours, startDateTime);
      const replaceWithResult = shouldReplaceHours(next.hours, result.hours, startDateTime);
      // if menu hours overlap, combine them.
      const combineBoth = shouldCombineHours(result.hours, next.hours);
      // Now that decision is made, update result if necessary.
      if (replaceWithNext) {
        result = next;
      } else if (replaceWithResult) {
        continue;
      } else if (combineBoth) {
        const newEndDate = DateTime.max(fromISO(result.endDate), fromISO(next.endDate)).toFormat(
          'yyyy-MM-dd',
        );
        result = { hours: result.hours.union(next.hours), endDate: newEndDate };
      } else {
        // Edge case: if they don't overlap, just take earlier one
        result = result.hours.start.toMillis() < next.hours.start.toMillis() ? result : next;
      }
    }
    return result;
  };

  /**
   * Calculate the hours for a station in the given timezone, given a list of all scheduled menu hours in UTC.
   *
   * @param allMenuHours - A list of all scheduled menu hours, each represented as an array of hours.
   * @returns Each day's station hours.
   */
  const computeStationHoursFromAllMenuHours = (allMenuHours: MenuHours[][]): Hours[] => {
    //
    if (!allMenuHours?.length) return [];
    const allMenuHoursLocalized: Hours[][] = localizeAllMenuHours(allMenuHours);
    const menuHoursByDay: Record<string, IntervalEntry[]> =
      groupMenuHoursByDay(allMenuHoursLocalized);
    const stationHours: Hours[] = [];
    const menuHourEntries = Object.entries(menuHoursByDay);
    for (const [startDate, intervals] of menuHourEntries) {
      const { hours: stationHoursForDay, endDate } = computeStationHoursForSingleDay(
        intervals,
        startDate,
      );
      if (!stationHoursForDay) continue;
      // Convert Interval to Hours object
      const hours: Hours = {
        date: { start: startDate, end: endDate },
        hours: parseIntervalToHours(stationHoursForDay),
      };
      const startsAfterToday = fromISO(startDate) > local().startOf('day');
      const hasHoursToday = !startsAfterToday && !!findTodaysHours([hours]).hours;
      if (startsAfterToday || hasHoursToday) {
        stationHours.push(hours);
      }
    }
    return stationHours;
  };

  /**
   * Convert a set of station or menu hours in UTC to local hours.
   * @param hours - The hours to convert in UTC. Must have entry.date (date).
   * @returns The hours in the station timezone.
   */
  const convertUtcHours = (hours: Hours[]): Hours[] => {
    if (!hours?.length) return [];
    const newHours: Hours[] = hours.map((entry) => {
      if (!entry.hours) return entry;
      const [utcStart, utcEnd] = entry.hours.split('-');
      const { time: startTime, date: startDate } = utcHourToLocalHour(utcStart, entry.date.start);
      const { time: endTime, date: endDate } = utcHourToLocalHour(utcEnd, entry.date.end);
      return {
        hours: `${startTime}-${endTime}`,
        id: entry.id,
        date: { start: startDate, end: endDate },
      };
    });

    return newHours;
  };

  /**
   * Convert a single UTC hour to local hours.
   * @param utcTime - The UTC time to convert, as a string in the format "HH:mm".
   * @param date - The date of the hours in UTC.
   * @returns Data containing `time` (the time localized to the station timezone in HH:mm format),
   *          `date` (the date localized to the station timezone in yyyy-MM-dd format).
   */
  const utcHourToLocalHour = (utcTime: string, date: string): { time: string; date: string } => {
    // Check if the timezone is valid
    if (!IANAZone.isValidZone(timezone)) {
      throw new Error('Invalid timezone provided');
    }

    let utcDateTime;
    const [hour, minute] = utcTime.split(':');
    const originalDate = DateTime.fromISO(date, { zone: 'utc', setZone: true });
    utcDateTime = originalDate.set({
      hour: parseInt(hour),
      minute: parseInt(minute),
      second: 0,
    });
    const localDateTime = utcDateTime.setZone(timezone);
    return { time: localDateTime.toFormat('HH:mm'), date: localDateTime.toFormat('yyyy-MM-dd') };
  };

  const areTodaysHoursAlreadyPassed = (hours: string): boolean => {
    if (!hours) return false;
    const [start, end] = hours.split('-');
    const [startHour] = start.split(':');
    const [endHour, endMinute] = end.split(':');
    if (isOpen24Hours(hours)) return false;
    // Hours will definitely be today
    let closingDateTime;
    if (parseInt(startHour) > parseInt(endHour)) {
      // ex: 7:00-1:00 - will close tomorrow
      closingDateTime = local()
        .plus({ days: 1 })
        .set({
          hour: parseInt(endHour),
          minute: parseInt(endMinute),
          second: 0,
        });
    } else {
      closingDateTime = local().set({
        hour: parseInt(endHour),
        minute: parseInt(endMinute),
        second: 0,
      });
    }
    const now = local();
    return closingDateTime.toMillis() < now.toMillis();
  };

  const format24HourTime = (hours: string): string => {
    if (!hours) return '';

    const [openHour, openMinute]: string[] = hours.split('-')[0].split(':');
    const [closingHour, closingMinute]: string[] = hours.split('-')[1].split(':');
    const openAMPM: string = parseInt(openHour) < 12 || parseInt(openHour) === 24 ? 'AM' : 'PM';
    const endAMPM: string =
      parseInt(closingHour) < 12 || parseInt(closingHour) === 24 ? 'AM' : 'PM';
    const openHourFormatted = Math.floor(parseInt(openHour) % 12 || 12);
    const endHourFormatted = Math.floor(parseInt(closingHour) % 12 || 12);
    return `${openHourFormatted}:${openMinute.trim()}${openAMPM.toLowerCase()} - ${endHourFormatted}:${closingMinute.trim()}${endAMPM.toLowerCase()}`;
  };

  const getCurrentLocalTime = (): number => {
    const { minute, hour } = local();
    const formattedMinute = minute < 10 ? `0${minute}` : minute;
    return parseInt(`${hour}${formattedMinute}`);
  };
  const isCurrentlyOpen = (hours: string): boolean => {
    if (!hours) return false;
    const [start, end] = hours.split('-');
    const [startHour, startMinute] = start.split(':');
    const [endHour, endMinute] = end.split(':');
    if (isOpen24Hours(hours)) return true;
    // Hours will definitely be today
    const openingDateTime = local().set({
      hour: parseInt(startHour),
      minute: parseInt(startMinute),
      second: 0,
    });
    let closingDateTime;
    if (parseInt(startHour) > parseInt(endHour)) {
      // ex: 7:00-1:00 - will close tomorrow
      closingDateTime = local()
        .plus({ days: 1 })
        .set({
          hour: parseInt(endHour),
          minute: parseInt(endMinute),
          second: 0,
        });
    } else {
      closingDateTime = local().set({
        hour: parseInt(endHour),
        minute: parseInt(endMinute),
        second: 0,
      });
    }
    const now = local();
    return (
      openingDateTime.toMillis() < now.toMillis() && now.toMillis() < closingDateTime.toMillis()
    );
  };
  const isOpenLaterToday = (hours: string): boolean => {
    if (!hours) return false;
    const open = parseInt(hours.split('-')[0].split(':').join(''));
    const currentLocalTime = getCurrentLocalTime();
    if (currentLocalTime < open) return true;
    return false;
  };

  const findTodaysHours = (hours: Hours[]): { hours: string; openNow: boolean } => {
    if (!hours?.length) return { hours: null, openNow: false };
    const now = local();
    const currentDate = now.toFormat('yyyy-MM-dd');
    const found = hours.find((hour) => {
      return (
        hour.date.start === currentDate &&
        (!areTodaysHoursAlreadyPassed(hour.hours) || isOpen24Hours(hour.hours))
      );
    });
    if (!found) return { hours: null, openNow: false };
    else return { hours: found.hours, openNow: isCurrentlyOpen(found.hours) };
  };

  const getFormattedDateFromMillis = (milliseconds: number): string => {
    if (!milliseconds) return 'No date provided';
    return DateTime.fromMillis(milliseconds)
      .toFormat('hh:mma MMM dd, yyyy')
      .replace(/AM|PM/, (m) => m.toLowerCase());
  };

  const findNextOpeningDate = (
    hours: Hours[],
    tomorrow?: string,
  ): { date: string; hours: string } => {
    if (!hours?.length) return { date: null, hours: null };
    const now = local();
    const currentDate = now.startOf('day');
    hours.sort((a, b) => fromISO(a.date.start).toMillis() - fromISO(b.date.start).toMillis());
    const nextOpening = hours.find((hour) => {
      return fromISO(hour.date.start) > currentDate;
    });
    if (!nextOpening) {
      return { date: null, hours: null };
    }
    const nextOpeningDate = fromISO(nextOpening.date.start);
    let dateString;
    if (
      tomorrow &&
      nextOpeningDate.toFormat('yyyy-MM-dd') === now.plus({ days: 1 }).toFormat('yyyy-MM-dd')
    ) {
      dateString = tomorrow;
    } else {
      dateString = nextOpeningDate.toFormat('EEEE, MMM dd, yyyy');
    }
    return { date: dateString, hours: nextOpening.hours };
  };

  return {
    convertUtcHours,
    utcHourToLocalHour,
    areTodaysHoursAlreadyPassed,
    format24HourTime,
    getCurrentLocalTime,
    isCurrentlyOpen,
    isOpenLaterToday,
    findTodaysHours,
    getFormattedDateFromMillis,
    findNextOpeningDate,
    computeStationHoursFromAllMenuHours,
    isToday,
    isInPast,
    areMenuHoursInPast,
    isOpen24Hours,
    filterPastMenuHours,
  };
};
